import { useCallback, useEffect, useRef, useState } from 'react'

type BarcodeDetectorInstance = {
  detect: (source: HTMLVideoElement) => Promise<Array<{ rawValue?: string }>>
}

type BarcodeDetectorConstructor = new (options?: { formats: string[] }) => BarcodeDetectorInstance

const getBarcodeDetector = (): BarcodeDetectorConstructor | undefined =>
  (typeof window !== 'undefined' ? (window as Window & { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector : undefined)

export interface UseQrCameraScannerOptions {
  active: boolean
  videoRef: React.RefObject<HTMLVideoElement | null>
  onRawValue: (raw: string) => void
  setStatus: (message: string) => void
  setCameraError: (message: string | null) => void
  /** Shown when native BarcodeDetector is unavailable (jsQR fallback). */
  compatibilityStatus?: string
  /** Invoked at the start of each camera session (e.g. clear prior scan messages). */
  onSessionStart?: () => void
}

export interface UseQrCameraScannerResult {
  torchSupported: boolean
  torchOn: boolean
  toggleTorch: () => Promise<void>
  flipCameraSupported: boolean
  flipCamera: () => void
}

/**
 * Shared QR camera loop: BarcodeDetector when available, otherwise jsQR on a canvas (lazy-loaded).
 */
export function useQrCameraScanner({
  active,
  videoRef,
  onRawValue,
  setStatus,
  setCameraError,
  compatibilityStatus = 'Camera ready. Using compatibility scanner.',
  onSessionStart,
}: UseQrCameraScannerOptions): UseQrCameraScannerResult {
  const streamRef = useRef<MediaStream | null>(null)
  const animationRef = useRef<number | null>(null)
  const activeRef = useRef(false)
  const onRawValueRef = useRef(onRawValue)
  const setStatusRef = useRef(setStatus)
  const setCameraErrorRef = useRef(setCameraError)
  const onSessionStartRef = useRef(onSessionStart)
  const compatibilityRef = useRef(compatibilityStatus)

  const [facingUser, setFacingUser] = useState(false)
  const [torchSupported, setTorchSupported] = useState(false)
  const [torchOn, setTorchOn] = useState(false)
  const [flipCameraSupported, setFlipCameraSupported] = useState(false)

  useEffect(() => {
    onRawValueRef.current = onRawValue
  }, [onRawValue])
  useEffect(() => {
    setStatusRef.current = setStatus
  }, [setStatus])
  useEffect(() => {
    setCameraErrorRef.current = setCameraError
  }, [setCameraError])
  useEffect(() => {
    onSessionStartRef.current = onSessionStart
  }, [onSessionStart])
  useEffect(() => {
    compatibilityRef.current = compatibilityStatus
  }, [compatibilityStatus])

  const stopInternal = useCallback(() => {
    activeRef.current = false
    setTorchOn(false)
    setTorchSupported(false)
    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = null
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop()
      }
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
  }, [videoRef])

  const flipCamera = useCallback(() => {
    setFacingUser((v) => !v)
  }, [])

  const toggleTorch = useCallback(async () => {
    const stream = streamRef.current
    const track = stream?.getVideoTracks()[0]
    if (!track?.applyConstraints) return
    const caps = track.getCapabilities?.() as MediaTrackCapabilities & { torch?: boolean } | undefined
    if (!caps?.torch) return
    const next = !torchOn
    try {
      await (track as any).applyConstraints({ advanced: [{ torch: next }] })
      setTorchOn(next)
    } catch {
      setTorchOn(false)
    }
  }, [torchOn])

  useEffect(() => {
    if (!active) {
      stopInternal()
      return
    }

    const videoEl = videoRef.current
    if (!videoEl) return

    onSessionStartRef.current?.()
    setCameraErrorRef.current(null)

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraErrorRef.current('Camera access is not supported in this browser.')
      return
    }

    setStatusRef.current('Starting camera...')

    let cancelled = false

    const run = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facingUser ? 'user' : 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        })

        if (cancelled) {
          for (const t of stream.getTracks()) t.stop()
          return
        }

        streamRef.current = stream
        videoEl.srcObject = stream
        await videoEl.play()
        activeRef.current = true
        setStatusRef.current('Point camera at a QR code')

        const track = stream.getVideoTracks()[0]
        const caps = track?.getCapabilities?.() as MediaTrackCapabilities & { torch?: boolean } | undefined
        setTorchSupported(Boolean(caps?.torch))
        setTorchOn(false)

        try {
          const devices = await navigator.mediaDevices.enumerateDevices()
          const videos = devices.filter((d) => d.kind === 'videoinput')
          setFlipCameraSupported(videos.length >= 2)
        } catch {
          setFlipCameraSupported(false)
        }

        const BarcodeDetectorCtor = getBarcodeDetector()
        const detector = BarcodeDetectorCtor ? new BarcodeDetectorCtor({ formats: ['qr_code'] }) : null
        if (!detector) {
          setStatusRef.current(compatibilityRef.current)
        }

        const fallbackCanvas = document.createElement('canvas')
        const fallbackContext = fallbackCanvas.getContext('2d', { willReadFrequently: true })
        let frameCounter = 0
        // jsQR default export; cache after first dynamic import (BarcodeDetector fallback path).
        let jsQrDecode: ((data: Uint8ClampedArray, w: number, h: number, opts?: unknown) => unknown) | null = null

        const tick = async () => {
          if (!activeRef.current || !videoRef.current) return

          try {
            const video = videoRef.current
            if (video.readyState >= 2) {
              let rawValue = ''

              if (detector) {
                const barcodes = await detector.detect(video)
                if (Array.isArray(barcodes) && barcodes.length > 0) {
                  rawValue = String(barcodes[0]?.rawValue || '')
                }
              }

              if (!rawValue && fallbackContext) {
                frameCounter += 1
                if (frameCounter % 3 === 0) {
                  const width = video.videoWidth
                  const height = video.videoHeight
                  if (width > 0 && height > 0) {
                    if (!jsQrDecode) {
                      const mod = await import('jsqr')
                      jsQrDecode = mod.default as (data: Uint8ClampedArray, w: number, h: number, opts?: unknown) => unknown
                    }
                    fallbackCanvas.width = width
                    fallbackCanvas.height = height
                    fallbackContext.drawImage(video, 0, 0, width, height)
                    const imageData = fallbackContext.getImageData(0, 0, width, height)
                    const decoded = jsQrDecode!(imageData.data, width, height, {
                      inversionAttempts: 'dontInvert',
                    }) as { data?: string } | null
                    rawValue = decoded?.data || ''
                  }
                }
              }

              if (rawValue) {
                onRawValueRef.current(rawValue)
              }
            }
          } catch {
            // keep scanning
          }

          animationRef.current = window.requestAnimationFrame(() => {
            void tick()
          })
        }

        animationRef.current = window.requestAnimationFrame(() => {
          void tick()
        })
      } catch (err: unknown) {
        if (!cancelled) {
          const domName =
            err && typeof err === 'object' && 'name' in err ? String((err as DOMException).name) : ''
          const dismissed =
            domName === 'NotAllowedError' ||
            domName === 'PermissionDismissedError' ||
            domName === 'AbortError'
          const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
          const looksLikeIOS =
            /iP(ad|hone|od)/i.test(ua) || (ua.includes('Mac') && typeof document !== 'undefined' && 'ontouchend' in document)

          if (dismissed && looksLikeIOS) {
            setCameraErrorRef.current(
              'Camera was not enabled. Open Settings → Safari → Camera (or tap the lock icon in the address bar), allow access for this site, then try again.',
            )
          } else if (dismissed) {
            setCameraErrorRef.current(
              'Camera access was cancelled or blocked. When your browser asks for permission, choose Allow, then try again.',
            )
          } else {
            setCameraErrorRef.current('Unable to access camera. Please allow camera permission and retry.')
          }
        }
      }
    }

    void run()

    return () => {
      cancelled = true
      stopInternal()
    }
  }, [active, facingUser, stopInternal, videoRef])

  return {
    torchSupported,
    torchOn,
    toggleTorch,
    flipCameraSupported,
    flipCamera,
  }
}
